// src/pages/fiscalization/FiscalizationDashboardPage.tsx
// Operations dashboard: today's fiscal summary, which fiscal points have an
// open shift, and the most recent receipts — the "is everything reaching the
// tax authority" home screen. Mirrors BillingDashboardPage's stat-card +
// quick-links shape (docs/ux/... billing area is the adjacency target).
//
// Covers: GetFiscalSummary (today), ListFiscalPoints (status), ListFiscalReceipts (recent).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useFiscalizationReport, useFiscalizationFiscalPoint, useFiscalizationFiscalReceipt, useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, Badge, ErrorAlert } from '@react/components';
import type { FiscalSummary } from '@core/types/fiscalization';
import { formatDateTime, formatMoney, humanizeEnum } from '@/features/fiscalization/format';
import { mapFiscalPointStatusToVariant, mapReceiptStatusToVariant } from '@/features/fiscalization/mapStatusToVariant';

const QUICK_LINKS = [
  { label: 'fiscalization.points.title', path: '/fiscalization/points' },
  { label: 'fiscalization.receipts.title', path: '/fiscalization/receipts' },
  { label: 'fiscalization.settings.title', path: '/fiscalization/settings' },
  { label: 'fiscalization.reports.title', path: '/fiscalization/reports/summary' },
];

export const FiscalizationDashboardPage = observer(() => {
  const reportStore = useFiscalizationReport();
  const pointStore = useFiscalizationFiscalPoint();
  const receiptStore = useFiscalizationFiscalReceipt();
  const { t } = useI18n();
  const [summary, setSummary] = useState<FiscalSummary | null>(null);

  useEffect(() => {
    reportStore.GetFiscalSummary().then((res) => setSummary(res.summary ?? null)).catch(() => {});
    pointStore.ListFiscalPoints({ pagination: { cursor: '', limit: 20, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
    receiptStore.ListFiscalReceipts({ pagination: { cursor: '', limit: 10, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const openPoints = pointStore.items.filter((p) => !!p.current_shift_id);

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('fiscalization.dashboard.title')}</h1>

      {pointStore.error ? <ErrorAlert error={pointStore.error} dismissible onDismiss={() => pointStore.clearError()} /> : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.dashboard.openShifts')}</p><p className="text-2xl font-bold text-foreground">{openPoints.length}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.dashboard.fiscalizedToday')}</p><p className="text-2xl font-bold text-foreground">{summary?.fiscalized_count ?? '--'}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.dashboard.failedToday')}</p><p className="text-2xl font-bold text-destructive">{summary?.failed_count ?? '--'}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.dashboard.pendingToday')}</p><p className="text-2xl font-bold text-foreground">{summary?.pending_count ?? '--'}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>{t('fiscalization.dashboard.fiscalPoints')}</CardTitle></CardHeader>
          <CardContent>
            {pointStore.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('fiscalization.points.empty')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {pointStore.items.map((p) => (
                  <li key={p.entity?.id} className="py-2 flex items-center justify-between text-sm">
                    <Link to="/fiscalization/points" className="text-foreground hover:text-primary">{p.name}</Link>
                    <div className="flex items-center gap-2">
                      {p.current_shift_id && <Badge variant="success" size="sm">{t('fiscalization.shifts.currentlyOpen')}</Badge>}
                      <Badge variant={mapFiscalPointStatusToVariant(p.status)} size="sm">{humanizeEnum(p.status, 'FISCAL_POINT_STATUS_')}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('fiscalization.dashboard.recentReceipts')}</CardTitle></CardHeader>
          <CardContent>
            {receiptStore.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('fiscalization.receipts.empty')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {receiptStore.items.map((r) => (
                  <li key={r.entity?.id} className="py-2 flex items-center justify-between text-sm">
                    <Link to="/fiscalization/receipts" className="text-foreground hover:text-primary">{formatMoney(r.total_amount)}</Link>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{formatDateTime(r.created_at)}</span>
                      <Badge variant={mapReceiptStatusToVariant(r.status)} size="sm">{humanizeEnum(r.status, 'FISCAL_RECEIPT_STATUS_')}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>{t('fiscalization.dashboard.navigation')}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <span className="text-sm font-medium text-foreground">{t(link.label)}</span>
                <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default FiscalizationDashboardPage;
